import { format } from "date-fns";
import React, { useContext, useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import Box from "../../../../common/box/Box";
import Divider from "../../../../common/divider/Divider";
import { InfoBarContext } from "../../../../common/info/index";
import { MessageContext } from "../../../../common/message/index";
import { publish } from "../../../../eventBus";
import { GET, POST } from "../../../../utils/fetchUtil";
import { cleanJoin, cutShort } from "../../../../utils/stringUtil";
import PdfFormErrors from "../../submission/pdf/PdfFormErrors";
import DocumentUpload, { UPLOAD_STATUS } from "../../submission/upload/DocumentUpload";
import { reducer } from "./RequestLetterReducer";
import PdfFormModal from "../../submission/pdf/PdfFormModal";
import Content from "../../../../common/content/Content";
import { Level } from "../../../../common/level";
import { selectAllInputsQuery } from "../../submission/pdf/PdfForm";

export const hasUploadedDocs = (docs) =>
  docs.filter((doc) => doc.uploadStatus === UPLOAD_STATUS.SUCCESS).length > 0;

function RequestLetterPage({ isContainer = true }) {
  const [state, dispatch] = useReducer(reducer, null);
  const { t } = useTranslation();
  const { id, uid } = useParams();
  const infoBar = useContext(InfoBarContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const message = useContext(MessageContext);
  const [buttonLabel, setButtonLabel] = useState(t("requests.overview.letter.request.create"));
  const history = useHistory();

  useEffect(() => {
    GET(`/requests/${id}/submissions/${uid}/letter`, (request) => {
      dispatch({ type: "init", payload: request });
    });
  }, [id, uid]);

  useEffect(() => {
    if (state && state.hasOwnProperty("color")) {
      setSubmitEnabled(state.color === "green");
    }
  }, [state]);

  const onPdfFormModalClose = () => {
    message.toggle({
      title: t("requests.overview.letter.pdfModal.close.title"),
      content: t("requests.overview.letter.pdfModal.close.content"),
      onYes: () => {
        message.toggle({});
        Object.keys(state.pdfForm.fieldDefinitions).forEach((id) => {
          selectAllInputsQuery(document).forEach((e) =>
            e.classList.remove("has-border-red-1-important")
          );
        });
        setIsModalOpen(false);
        dispatch({ type: "reset" });
      },
      onNo: () => message.toggle({}),
    });
  };

  const onPdfFormModalAccept = () => {
    publish({
      type: "validate-pdf-form",
      payload: {},
      onReturn: (validationErrors) => {
        if (validationErrors.length === 0) {
          setIsModalOpen(false);
          state.color = Object.keys(state.pdfForm.validationErrors).length > 0 ? "red" : "green";
          dispatch({ type: "update-documents" });
          setButtonLabel(t("requests.overview.letter.request.edit"));
        } else {
          if (!infoBar.isOpen) {
            infoBar.toggle(t("requests.submission.pdf.pdfFormErrorInfo.errorMessageTitle"), () => (
              <PdfFormErrors errors={JSON.stringify(validationErrors)} />
            ));
          }
        }
      },
    });
  };

  const submit = () => {
    message.toggle({
      title: t("requests.overview.letter.pdfModal.submit.title"),
      content: t("requests.overview.letter.pdfModal.submit.content"),
      onYes: () => {
        message.toggle({});
        message.toggle({ title: "", content: t("requests.overview.submitted.wait.label"), hideNo: true, hideYes: true, isWait: true, hideX: true });
        POST(
          `/requests/${id}/submissions/${uid}/letter`,
          state,
          () => {
            setIsModalOpen(false);
            dispatch({ type: "reset" });
            message.toggle({});
            message.toggle({
              title: t("modalMessage.titleInfo"),
              content: t("requests.overview.letter.pdfModal.submit.okMessage"),
              hideNo: true,
              yesLabel: t("modalMessage.okLabel"),
              onYes: () => {
                message.toggle({});
                history.push("/requests", "/requests/#t1");
              },
            });
          },
          (error) => {
            message.toggle({
              title: t("requests.overview.supplement.infoBar.errorTitle"),
              content: error,
              hideNo: true,
              yesLabel: t("modalMessage.okLabel"),
              onYes: () => {
                message.toggle({});
              },
            });
          }
        );
      },
      onNo: () => message.toggle({}),
    });
  };

  const onSubmit = () => {
    if (!hasUploadedDocs(state.additionalDocs)) {
      message.toggle({
        title: t("requests.overview.letter.pdfModal.submit.title"),
        content: t("requests.overview.letter.pdfModal.submit.contentSubmissions"),
        onYes: () => {
          message.toggle({});
          submit();
        },
        onNo: () => message.toggle({}),
      });
    } else {
      submit();
    }
  };

  return (
    <Content isContainer={isContainer}>
      {state && (
        <>
          <PdfFormModal
            title={t("requests.overview.letter.pdfModal.title")}
            isOpen={isModalOpen}
            state={state}
            dispatch={dispatch}
            onClose={onPdfFormModalClose}
            onAccept={onPdfFormModalAccept}
          />
          <div className="section is-size-5">
            <div className="title has-text-centered p-4">
              {state.title} - {t("requests.overview.letter.request.divider")}
            </div>
            <p className="is-size-6">{cutShort(state.info, 200, "...")}</p>
            <div className="is-size-6">
              {t("requests.overview.letter.subject")}: {state.subject}
            </div>
            <div className="is-size-6">
              {t("requests.overview.letter.submissionDate")}:{" "}
              {format(new Date(state.submissionDate), "dd.MM.yyyy HH:mm")}
            </div>
            <Divider label={t("requests.overview.letter.request.divider")} />
            <Box
              statusIndicatorPosition="left"
              className="is-size-5"
              statusIndicatorColor={state.color}
            >
              <Level>
                <Level.Side align="left">
                  {t("requests.overview.letter.request.documentTitle")}
                </Level.Side>
                <Level.Side align="right" className="is-flex">
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    className="button is-pulled-left"
                    onClick={() => {
                      setIsModalOpen(true);
                      dispatch({ type: "save-previous-field-values" });
                    }}
                  >
                    {buttonLabel}{" "}
                    {t("requests.overview.letter.request.divider").toLocaleLowerCase()}
                  </a>
                </Level.Side>
              </Level>
            </Box>
            <Divider label={t("requests.overview.letter.additionalDocs.divider")} />
            <DocumentUpload
              dispatchUpdateAction={() => dispatch({ type: "update-documents" })}
              documents={state.additionalDocs}
              minDocumentsAttached={state.uploadConstraints.minDocumentsAttached}
              acceptDocumentTypes={state.uploadConstraints.acceptDocumentTypes}
              maxDocumentSizeInMb={state.uploadConstraints.maxDocumentSizeInMb}
              formDataAsJson={{ id: uid }}
            />
            <Content className="has-text-centered mt-6">
              <button
                disabled={!submitEnabled}
                className={cleanJoin(
                  "button",
                  "pdf-form-action-button",
                  "no-focus-outline",
                  submitEnabled
                    ? "has-background-green-on-hover has-text-white-on-hover has-opacity-71-on-hover has-border-green-1"
                    : ""
                )}
                onClick={onSubmit}
              >
                {t("requests.overview.letter.submit")}
              </button>
            </Content>
          </div>
        </>
      )}
    </Content>
  );
}

export default RequestLetterPage;
